import { useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Download, Save } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { BlockPalette } from "@/components/reports/BlockPalette";
import { ReportCanvas } from "@/components/reports/ReportCanvas";
import { BlockSettings } from "@/components/reports/BlockSettings";
import {
  type ReportBlock,
  type BlockType,
  type ReportSettings,
  DEFAULT_SETTINGS,
  REPORT_BLOCKS,
  PRESET_TEMPLATES,
} from "@/lib/reports/block-types";
import { fetchReportData } from "@/lib/reports/data-fetcher";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function generateId() {
  return crypto.randomUUID();
}

export default function ReportBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("template");
  const queryClient = useQueryClient();
  const { orgId, familyId } = useTenant();

  const [blocks, setBlocks] = useState<ReportBlock[]>([]);
  const [settings, setSettings] = useState<ReportSettings>({ ...DEFAULT_SETTINGS });
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [generating, setGenerating] = useState(false);

  // Load template if editing
  useQuery({
    queryKey: ["report-template", templateId],
    queryFn: async () => {
      if (!templateId) return null;
      const { data, error } = await supabase
        .from("report_templates")
        .select("*")
        .eq("id", templateId)
        .single();
      if (error) throw error;
      if (data) {
        setBlocks(data.blocks as ReportBlock[]);
        setSettings(data.settings as ReportSettings);
        setTemplateName(data.name);
        setTemplateDesc(data.description || "");
      }
      return data;
    },
    enabled: !!templateId,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleAddBlock = useCallback((blockType: BlockType) => {
    const def = REPORT_BLOCKS[blockType];
    const newBlock: ReportBlock = {
      id: generateId(),
      type: blockType,
      config: { ...def.defaultConfig },
    };
    setBlocks((prev) => [...prev, newBlock]);
    setSelectedBlockId(newBlock.id);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      const oldIndex = blocks.findIndex((b) => b.id === (active.id as string));
      const newIndex = blocks.findIndex((b) => b.id === (over.id as string));
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setBlocks((prev) => arrayMove(prev, oldIndex, newIndex));
      }
    },
    [blocks]
  );

  const handleRemoveBlock = (id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    if (selectedBlockId === id) setSelectedBlockId(null);
  };

  const handleUpdateConfig = (blockId: string, config: Record<string, unknown>) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, config } : b))
    );
  };

  const handleLoadPreset = (presetIndex: number) => {
    const preset = PRESET_TEMPLATES[presetIndex];
    if (!preset) return;
    const newBlocks: ReportBlock[] = preset.blocks.map((type) => ({
      id: generateId(),
      type,
      config: { ...REPORT_BLOCKS[type].defaultConfig },
    }));
    setBlocks(newBlocks);
    setSelectedBlockId(null);
    toast.success(`Loaded "${preset.name}" template`);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!orgId || !templateName.trim()) throw new Error("Missing data");
      const payload = {
        org_id: orgId,
        family_id: familyId || null,
        name: templateName.trim(),
        description: templateDesc.trim() || null,
        blocks,
        settings,
        updated_at: new Date().toISOString(),
      };
      if (templateId) {
        const { error } = await supabase
          .from("report_templates")
          .update(payload)
          .eq("id", templateId);
        if (error) throw error;
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        const { error } = await supabase
          .from("report_templates")
          .insert({ ...payload, created_by: user?.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Template saved");
      setSaveDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["report-templates"] });
    },
    onError: () => toast.error("Failed to save template"),
  });

  const handleGeneratePdf = async () => {
    if (!orgId || blocks.length === 0) return;
    setGenerating(true);
    try {
      const data = await fetchReportData(blocks, settings, orgId, familyId);
      // Dynamic import to avoid bundling @react-pdf/renderer in the main chunk
      const [{ pdf }, { ReportPdfDocument }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("@/components/reports/ReportPdf"),
      ]);
      const blob = await pdf(
        ReportPdfDocument({ blocks, settings, data })
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(settings.headerText || "Report").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId) || null;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b bg-background">
        <Button variant="ghost" size="sm" onClick={() => navigate("/portfolio/reports")}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Period:</Label>
          <Input
            type="date"
            value={settings.periodStart}
            onChange={(e) => setSettings({ ...settings, periodStart: e.target.value })}
            className="h-7 w-[130px] text-xs"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="date"
            value={settings.periodEnd}
            onChange={(e) => setSettings({ ...settings, periodEnd: e.target.value })}
            className="h-7 w-[130px] text-xs"
          />
        </div>

        <div className="flex-1" />

        <Select onValueChange={(v) => handleLoadPreset(parseInt(v))}>
          <SelectTrigger className="w-[180px] h-7 text-xs">
            <SelectValue placeholder="Load preset..." />
          </SelectTrigger>
          <SelectContent>
            {PRESET_TEMPLATES.map((p, i) => (
              <SelectItem key={i} value={String(i)}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={() => setSaveDialogOpen(true)} disabled={blocks.length === 0}>
          <Save className="h-3.5 w-3.5 mr-1" />
          Save
        </Button>
        <Button size="sm" onClick={handleGeneratePdf} disabled={blocks.length === 0 || generating}>
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
          Generate PDF
        </Button>
      </div>

      {/* Three-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        <BlockPalette canvasBlocks={blocks} onAddBlock={handleAddBlock} />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <ReportCanvas
            blocks={blocks}
            selectedBlockId={selectedBlockId}
            onSelectBlock={setSelectedBlockId}
            onRemoveBlock={handleRemoveBlock}
          />
        </DndContext>
        <BlockSettings block={selectedBlock} onUpdateConfig={handleUpdateConfig} />
      </div>

      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{templateId ? "Update Template" : "Save as Template"}</DialogTitle>
            <DialogDescription>Save this report layout for future use.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Quarterly Client Review"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={templateDesc}
                onChange={(e) => setTemplateDesc(e.target.value)}
                placeholder="Brief description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!templateName.trim() || saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
